import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Badge, Button, ProgressBar } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import {
  listMcUsersForPmAdmin,
  listPersonalManagers,
  setUserPersonalManager,
} from '@/services/personalManagerService'
import type { McUserForPmAdmin, PersonalManagerListItem } from '@/types'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import { Search, UserCheck, Settings, AlertTriangle, Building2, Users, ClipboardCheck, ShieldCheck, CheckCircle2, Trophy } from 'lucide-react'

function ratio(fact: number, plan: number): number | null {
  return plan > 0 ? (fact / plan) * 100 : null
}

function pctTone(value: number | null): 'success' | 'warning' | 'error' | 'neutral' {
  if (value == null) return 'neutral'
  if (value >= 95) return 'success'
  if (value >= 75) return 'warning'
  return 'error'
}

function displayName(item: { manager_name: string | null; tg_username?: string | null; user_id?: string; manager_user_id?: string }) {
  return item.manager_name || item.tg_username || (item.user_id ?? item.manager_user_id ?? '').slice(0, 8)
}

export function PersonalManagersPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { getAccessLevel } = useManagementCompanySimple(user?.id)

  const [items, setItems] = useState<PersonalManagerListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [isAdvanced, setIsAdvanced] = useState(false)
  const [showRolesAdmin, setShowRolesAdmin] = useState(false)
  const [mcUsers, setMcUsers] = useState<McUserForPmAdmin[]>([])
  const [mcUsersLoading, setMcUsersLoading] = useState(false)
  const [mcUsersError, setMcUsersError] = useState<string | null>(null)
  const [busyUserId, setBusyUserId] = useState<string | null>(null)

  const reload = () => {
    setLoading(true)
    listPersonalManagers()
      .then(data => {
        setItems(data)
        setError(null)
      })
      .catch(err => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }

  const reloadMcUsers = () => {
    setMcUsersLoading(true)
    setMcUsersError(null)
    listMcUsersForPmAdmin()
      .then(setMcUsers)
      .catch(err => setMcUsersError(err instanceof Error ? err.message : String(err)))
      .finally(() => setMcUsersLoading(false))
  }

  useEffect(() => {
    reload()
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!user?.id) return
    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })
    return () => { cancelled = true }
  }, [user?.id, getAccessLevel])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(item =>
      displayName(item).toLowerCase().includes(q) ||
      (item.tg_username ?? '').toLowerCase().includes(q)
    )
  }, [items, search])

  const stats = useMemo(() => {
    const totalManagers = items.length
    const totalStudios = items.reduce((sum, item) => sum + item.studios_count, 0)
    const totalDone = items.reduce((sum, item) => sum + item.app_filled_fact, 0)
    const totalPlan = items.reduce((sum, item) => sum + item.app_filled_plan, 0)
    const totalProblem = items.reduce((sum, item) => sum + Math.max(item.app_filled_plan - item.app_filled_fact, 0), 0)
    return {
      totalManagers,
      totalStudios,
      totalDone,
      totalPlan,
      totalProblem,
      totalPct: ratio(totalDone, totalPlan),
    }
  }, [items])

  const handleTogglePm = async (u: McUserForPmAdmin, isPm: boolean) => {
    if (!isPm && u.partners_count > 0) {
      const ok = confirm(
        `У пользователя ${displayName(u)} есть ${u.partners_count} активных назначений. ` +
        'При снятии должности они будут закрыты сегодняшней датой. Продолжить?'
      )
      if (!ok) return
    }
    setBusyUserId(u.user_id)
    setMcUsersError(null)
    try {
      await setUserPersonalManager({ userId: u.user_id, isPm })
      reload()
      reloadMcUsers()
    } catch (err) {
      setMcUsersError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyUserId(null)
    }
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/management/personal-managers-hackathon')}
        >
          <Trophy size={16} className="mr-1" />
          Хакатон
        </Button>
        {isAdvanced && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/management/personal-managers-coverage')}
            >
              <Users size={16} className="mr-1" />
              Назначения
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const next = !showRolesAdmin
                setShowRolesAdmin(next)
                if (next && mcUsers.length === 0) reloadMcUsers()
              }}
            >
              <Settings size={16} className="mr-1" />
              {showRolesAdmin ? 'Скрыть' : 'Должности'}
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => navigate('/management/personal-managers')}
          className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-left transition hover:bg-primary/10"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <ClipboardCheck size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold">Проверки</div>
              <div className="text-xs text-muted-foreground">Стандартный дневной контроль по студиям</div>
            </div>
          </div>
        </button>
        <button
          onClick={() => navigate('/management/personal-managers-control')}
          className="rounded-xl border border-border bg-card p-4 text-left transition hover:bg-accent"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-emerald-100 p-2 text-emerald-600">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-sm font-semibold">Контроль</div>
              <div className="text-xs text-muted-foreground">Назначения, покрытие и управление ЛМ</div>
            </div>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <UserCheck size={14} className="text-primary" /> ЛМ
          </div>
          <div className="mt-1 text-2xl font-semibold">{stats.totalManagers}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Building2 size={14} className="text-amber-500" /> Студий
          </div>
          <div className="mt-1 text-2xl font-semibold">{stats.totalStudios}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 size={14} className="text-success" /> Выполнено
          </div>
          <div className="mt-1 text-2xl font-semibold text-success">{stats.totalDone}</div>
          <div className="text-xs text-muted-foreground">{stats.totalPct == null ? '—' : formatPercentage(stats.totalPct)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <AlertTriangle size={14} className="text-error" /> Проблемы
          </div>
          <div className="mt-1 text-2xl font-semibold text-error">{stats.totalProblem}</div>
          <div className="text-xs text-muted-foreground">из {stats.totalPlan}</div>
        </Card>
      </div>

      {isAdvanced && showRolesAdmin && (
        <Card>
          <div className="text-sm font-semibold mb-2">Назначение должности ЛМ</div>
          <div className="text-xs text-muted-foreground mb-3">
            ЛМ может быть только пользователь УК уровня <code>standard</code>.
          </div>
          {mcUsersError && (
            <div className="text-xs text-destructive mb-2 flex items-center gap-1">
              <AlertTriangle size={12} /> {mcUsersError}
            </div>
          )}
          {mcUsersLoading && <div className="text-xs text-muted-foreground">Загрузка...</div>}
          <div className="divide-y divide-border">
            {mcUsers.map(u => {
              const isPm = u.job_title === 'personal_manager'
              const isAdvancedUser = u.access_level === 'advanced'
              return (
                <div key={u.user_id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {displayName(u)}
                      {isPm && <Badge text="ЛМ" variant="success" className="ml-2 text-xs" />}
                      {isAdvancedUser && <Badge text="advanced" variant="secondary" className="ml-2 text-xs" />}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {u.tg_username ? `@${u.tg_username}` : u.email || 'без контакта'} · франчайзи: {u.partners_count}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={isPm ? 'outline' : 'secondary'}
                    disabled={busyUserId === u.user_id || isAdvancedUser}
                    onClick={() => handleTogglePm(u, !isPm)}
                  >
                    {isPm ? 'Снять' : 'Назначить'}
                  </Button>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      <Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск по ФИ или Telegram"
            className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
      </Card>

      {loading && <Card><div className="text-sm text-muted-foreground">Загрузка...</div></Card>}
      {error && <Card><div className="text-sm text-destructive">{error}</div></Card>}

      <div className="grid grid-cols-1 gap-3">
        {filtered.map(item => {
          const appPct = ratio(item.app_filled_fact, item.app_filled_plan)
          const royaltyPct = ratio(item.royalty_paid_fact_rub, item.royalty_paid_plan_rub)
          return (
            <Card key={item.manager_user_id} className="cursor-pointer overflow-hidden">
              <div onClick={() => navigate(`/management/personal-managers/${item.manager_user_id}`)} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{displayName(item)}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.tg_username ? `@${item.tg_username}` : 'без Telegram'}
                  </div>
                </div>
                <Badge text="ЛМ" variant="success" className="text-xs" />
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Users size={14} /> {item.partners_count} франч.
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Building2 size={14} /> {item.studios_count} студ.
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Заполнение</span>
                    <Badge text={appPct == null ? 'нет плана' : formatPercentage(appPct)} variant={pctTone(appPct)} className="text-xs" />
                  </div>
                  <ProgressBar value={appPct ?? 0} className="mt-2" />
                  <div className="mt-1 text-xs text-muted-foreground">
                    {item.app_filled_fact} / {item.app_filled_plan} студий
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Роялти</span>
                    <Badge text={royaltyPct == null ? 'нет плана' : formatPercentage(royaltyPct)} variant={pctTone(royaltyPct)} className="text-xs" />
                  </div>
                  <ProgressBar value={royaltyPct ?? 0} className="mt-2" />
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatCurrency(item.royalty_paid_fact_rub)} / {formatCurrency(item.royalty_paid_plan_rub)}
                  </div>
                  {item.royalty_offset_rub > 0.005 && (
                    <div
                      className="mt-0.5 text-[11px] text-emerald-700"
                      title="Долг, закрытый зачётом возврата, а не деньгами. В собираемость входит — обязательство погашено, — но показан отдельно."
                    >
                      из них зачётом {formatCurrency(item.royalty_offset_rub)}
                    </div>
                  )}
                  {item.royalty_rb_tax_offset_rub > 0.005 && (
                    <div
                      className="mt-0.5 text-[11px] text-emerald-700"
                      title="Налог, удержанный белорусским плательщиком и подтверждённый справкой. Обязательство погашено, поэтому входит в собираемость, но это не деньги на счёте."
                    >
                      из них зачётом РБ {formatCurrency(item.royalty_rb_tax_offset_rub)}
                    </div>
                  )}
                </div>
              </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
